<template lang="pug">
div
  j-dialog-container(
    :label="$t('common.sort')"
    :show-bottom="false"
    :show-action-button="true"
    v-model="value"
    :persistent="showConfirmReset"
    :isHierarchyModal="showConfirmReset"
    @on-close="$emit('on-close', $event)"
    @on-save-action-button="onSave"
    @on-reset-action-button="showConfirmReset=true"
  )
    j-master-change-order-list(
      v-if='!loading'
      :sortedItems="sortedItems"
      :unsortedItems="unsortedItems"
      @input="(event) => onInput(event)"
      :isSortedAuctionOdrnum="isSortedAuctionOdrnum"
      :disable-type="disableType"
    )
      template(v-for='(_, slot) of $scopedSlots' v-slot:[slot]='scope')
        slot(v-bind='scope', :name='slot')
    loader-component(
      v-if='loading'
      loader-type="text"
    )
    template(v-if="!hidePagination")
      pagination(
        :isDisabled="value"
        :currentPage="currentPage"
        :totalPage="totalPage"
        :totalItem="totalItem"
        :pageSize="pageSize"
        :items="items"
        @onChange ="onChangePage"
      )
  confirm-reset(
    :show-dialog="showConfirmReset"
    @on-close="confirmReset"
  )
</template>

<script>
import { defineComponent, ref, watch, getCurrentInstance } from 'vue'
import JMasterChangeOrderList from '../JMasterChangeOrderList/index.vue'
import JDialogContainer from '../JDialogContainer/index.vue'
import ConfirmReset from './ConfirmReset.vue'
import Pagination from '@/components/Pagination'
import LoaderComponent from '../LoaderComponent/index.vue'
import { usePagination } from '@/composables/useMaster'
import { showError, endpoints } from '@/utils'
import router from '@/router'

const JSortDialog = defineComponent({
  components: {
    JMasterChangeOrderList,
    JDialogContainer,
    ConfirmReset,
    Pagination,
    LoaderComponent
  },
  props: {
    value: {
      type: Boolean,
      default: false,
      required: true
    },
    endpoint: {
      type: String,
      required: true
    },
    queryParams: {
      default: () => ({}),
      required: false
    },
    isSortedAuctionOdrnum: { type: Boolean, default: false },
    disableType: {
      type: String,
      default: null
    },
    hidePagination: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const { $toast, $root } = instance.proxy
    const {
      currentPage,
      pageSize,
      totalPage,
      totalItem,
      items,
      getItems: getUnsortedItems
    } = usePagination(props.endpoint)
    const { items: sortedItems, getItems: getSortedItems } = usePagination(
      `${props.endpoint}`
    )
    const unsortedItems = ref()
    const idList = ref()
    const searchInfo = ref()
    const loading = ref(true)
    const sortedBy = ref(
      props.isSortedAuctionOdrnum ? 'auction_odrnum' : 'odrnum'
    )
    const getSortedByKey = ref(
      props.isSortedAuctionOdrnum
        ? 'is_sorted_by_auction_odrnum'
        : 'is_get_sorted'
    )
    const isVarietiesApi = props.endpoint === endpoints.MASTER_VARIETY

    const getItems = async () => {
      try {
        loading.value = true
        idList.value = sortedItems.value.map((item) => item.id)
        await getUnsortedItems({
          ...props.queryParams,
          active: true,
          ...(isVarietiesApi ? { ignore_item_active_filter: true } : {}),
          [getSortedByKey.value]: false,
          sorted_items: idList.value
        })
        unsortedItems.value = items.value
        loading.value = false
      } catch (e) {
        showError(e, $toast, $root.$t('message.get_data_failed'))
      }
    }

    const itemsSortVal = ref([])
    const showConfirmReset = ref(false)
    const onClose = () => {
      emit('on-close')
    }

    const onInput = (event) => {
      event.map((item) => {
        sortedItems.value = sortedItems.value.filter((it) => it.id !== item.id)
        if (item[sortedBy.value]) {
          sortedItems.value.push(item)
        }
      })
      itemsSortVal.value = event
    }
    const onSave = () => {
      if (itemsSortVal.value.length > 0)
        emit(
          'update-order-num',
          itemsSortVal.value
            .filter((item) => item[sortedBy.value])
            .map((item) => {
              return { id: item.id, [sortedBy.value]: item[sortedBy.value] }
            })
        )
    }

    const confirmReset = async (param) => {
      setTimeout(() => {
        showConfirmReset.value = false
      }, 0)

      if (param === 'yes') {
        currentPage.value = 1
        await getSortedItems({
          active: true,
          ...(isVarietiesApi ? { ignore_item_active_filter: true } : {}),
          [getSortedByKey.value]: true,
          gte_item_id: true,
          page: null,
          per_page: null
        })
        const sortedItemsDefault = sortedItems.value.map((item) => item.id)
        await getUnsortedItems({
          ...props.queryParams,
          active: true,
          ...(isVarietiesApi ? { ignore_item_active_filter: true } : {}),
          [getSortedByKey.value]: false,
          sorted_items: sortedItemsDefault
        })
        unsortedItems.value = items.value
      }
    }

    const onChangePage = async (page) => {
      currentPage.value = page
      await getItems()
    }

    watch(
      () => props.value,
      async (val) => {
        currentPage.value = 1
        if (val) {
          loading.value = true
          // reset sorted item list when open dialog
          const { search } = router.currentRoute.query
          searchInfo.value = search
          idList.value = null
          await getSortedItems({
            active: true,
            ...(isVarietiesApi ? { ignore_item_active_filter: true } : {}),
            [getSortedByKey.value]: true,
            page: null,
            per_page: null
          })
          getItems()
        }
      }
    )

    return {
      onClose,
      onSave,
      onInput,
      showConfirmReset,
      confirmReset,
      sortedItems,
      currentPage,
      pageSize,
      onChangePage,
      items,
      unsortedItems,
      totalPage,
      totalItem,
      loading
    }
  }
})

export default JSortDialog
</script>
